import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useBooking } from "../context/BookingContext";

function formatTime12(t) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
}

function slotEndTime(t) {
  const [h, m] = t.split(":").map(Number);
  const endH = (h + 1) % 24;
  return `${String(endH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const STATUS_STYLES = {
  upcoming:  "bg-blue-500/15 text-blue-400 border-blue-500/30",
  confirmed: "bg-green-500/15 text-green-400 border-green-500/30",
  completed: "bg-green-500/15 text-green-400 border-green-500/30",
  pending:   "bg-amber-500/15 text-amber-400 border-amber-500/30",
  failed:    "bg-red-500/15 text-red-400 border-red-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const { user, bookings, cancelBooking, setSelectedDate, clearSlots } = useBooking();
  const [tab, setTab] = useState("upcoming");
  const [cancelling, setCancelling] = useState(null);

  if (!user) {
    return (
      <div className="min-h-screen pt-24 flex flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-white/40">You need to be signed in to view your bookings.</p>
        <button onClick={() => navigate("/login")} className="btn-gold">Sign In</button>
      </div>
    );
  }

  const filtered = bookings.filter((b) => {
    if (tab === "all") return true;
    if (tab === "upcoming") return ["upcoming", "confirmed", "pending"].includes(b.status);
    return b.status === tab;
  });

  const handleBookAgain = (booking) => {
    setSelectedDate(booking.date);
    clearSlots();
    navigate("/book");
  };

  const handleCancel = (id) => {
    cancelBooking(id);
    setCancelling(null);
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pitch-700 to-pitch-500 flex items-center justify-center text-2xl font-bold text-white">
              {user.name[0].toUpperCase()}
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">{user.name}</h1>
              <p className="text-white/40 text-sm">+91 {user.phone}</p>
            </div>
          </div>

          {/* Quick action */}
          <button onClick={() => navigate("/book")} className="btn-gold mb-8 w-full sm:w-auto py-3 px-8">
            + Book a New Slot
          </button>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-dark-700 rounded-xl w-fit mb-6">
            {["upcoming", "completed", "cancelled", "all"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                  tab === t ? "bg-gold-500 text-black" : "text-white/50 hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Bookings */}
          <div className="space-y-4">
            <AnimatePresence mode="wait">
              {filtered.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="card-dark p-10 text-center text-white/30"
                >
                  No {tab} bookings yet.
                </motion.div>
              ) : (
                filtered.map((b, i) => {
                  const sorted = [...b.slots].sort();
                  const dateLabel = new Date(b.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
                  return (
                    <motion.div
                      key={b.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06, duration: 0.4 }}
                      className="card-dark p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-semibold">{b.id}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[b.status]}`}>
                            {b.status.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-white/70 text-sm">{dateLabel} · {formatTime12(sorted[0])} → {formatTime12(slotEndTime(sorted[sorted.length - 1]))}</p>
                        <p className="text-white/40 text-xs mt-0.5">{b.sport || "—"} · {b.phone}</p>
                      </div>
                      <div className="text-right sm:text-left flex sm:flex-col items-center sm:items-end gap-3">
                        <p className="text-gold-400 font-bold font-display text-lg">₹{b.total?.toLocaleString()}</p>
                        <div className="flex gap-2">
                          <button onClick={() => handleBookAgain(b)} className="btn-outline text-xs py-1.5 px-3">Book Again</button>
                          {["upcoming", "confirmed", "pending"].includes(b.status) && (
                            <button
                              onClick={() => setCancelling(b.id)}
                              className="text-xs py-1.5 px-3 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Cancel confirm modal */}
      <AnimatePresence>
        {cancelling && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="card-dark p-6 max-w-sm w-full"
            >
              <h3 className="font-bold text-white text-lg mb-2">Cancel Booking?</h3>
              <p className="text-white/40 text-sm mb-6">This action cannot be undone. Refund will be processed per our policy.</p>
              <div className="flex gap-3">
                <button onClick={() => handleCancel(cancelling)} className="flex-1 py-2.5 rounded-xl bg-red-500/20 border border-red-500/40 text-red-400 font-medium text-sm hover:bg-red-500/30 transition-colors">Yes, Cancel</button>
                <button onClick={() => setCancelling(null)} className="flex-1 btn-outline py-2.5 text-sm">Keep It</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
